import { useAccountStore } from '@/store/modules/account/store.js';
import { useGlobalStore } from '@/store/store.ts';
import { reactive, nextTick } from 'vue';
import { HTTP_RETRY_BUDGET_MS } from '@/constants/HttpRetry.ts';
import eventBus from '@/plugins/event-bus.ts';
// @ts-expect-error - auth0-js has no type definitions
import auth0 from 'auth0-js';
import jwtDecode from 'jwt-decode';
// @ts-expect-error - qs has no type definitions
import qs from 'qs';

import { useUserStore } from '@/store/modules/user/store.js';
import { wait } from '@/plugins/vue-wait.ts';
import { notify } from '@/plugins/vue-notification.ts';
import { ls } from '@/plugins/vue-ls.ts';
import { getCaching } from '@/plugins/caching.ts';
import router from '@/router/index.ts';
import { showConnectionTimeout, showUnauthenticated, showWarning } from '@/utils/toastr.js';
import TimedPageActions from '@/constants/TimedPageActions.ts';
import { navigateForContext, takeContextNavigationTarget } from '@/plugins/context-navigation.ts';
import { startAuthReadinessAttempt } from '@/telemetry/authReadiness.ts';
import { measureStartupPhase } from '@/telemetry/startupTiming.ts';
import authUserApi from '@/api/authUser.js';
import axios from './axios.js';
import i18n from './vue-i18n.js';
import ActivixDate from '../value-objects/ActivixDate.js';

interface AuthData {
    accessToken: string | null;
    expiration: number | string | null;
    impersonating: boolean | null;
    impersonateToken: string | null;
}

interface AuthResult {
    accessToken: string;
    expiresIn: number;
    singleUseToken?: boolean;
    appState?: { target?: string };
}

interface AppState {
    target?: string;
}

const ROUTES = {
    AUTH_ERROR: '/auth/error',
    AUTH_CALLBACK: '/auth/callback',
    NEW_PASSWORD: '/new-password',
    LANDING: '/landing',
    SANDBOX: '/sandbox',
    LOGOUT: '/auth/logout',
};

const STORAGE_KEYS = {
    TOKEN: 'auth_access_token',
    TOKEN_EXPIRATION: 'auth_access_token_expire',
    IMPERSONATE_TOKEN: 'auth_impersonate_token',
    IMPERSONATING: 'auth_impersonnating',
    LOCALE: 'currentLocale',
};

const WAIT_FOR_USER_INTERVAL_MS = 10;
const AUTH_USER_FETCH_ATTEMPTS = 2;
const AUTH_ACCOUNT_FETCH_PHASES = 1;
const AUTH_READINESS_RETRY_PHASES = AUTH_USER_FETCH_ATTEMPTS + AUTH_ACCOUNT_FETCH_PHASES;
export const AUTH_READINESS_RETRY_BUDGET_MS = HTTP_RETRY_BUDGET_MS * AUTH_READINESS_RETRY_PHASES;
export const AUTH_READINESS_REQUEST_ALLOWANCE_MS = 5000;
export const AUTH_READINESS_DEADLINE_MS =
    AUTH_READINESS_RETRY_BUDGET_MS + AUTH_READINESS_REQUEST_ALLOWANCE_MS;
const AUTH_ADMISSION_FETCH_PHASES = 1;
export const AUTH_STARTUP_RETRY_BUDGET_MS =
    AUTH_READINESS_RETRY_BUDGET_MS + HTTP_RETRY_BUDGET_MS * AUTH_ADMISSION_FETCH_PHASES;
export const AUTH_STARTUP_DEADLINE_MS =
    AUTH_STARTUP_RETRY_BUDGET_MS + AUTH_READINESS_REQUEST_ALLOWANCE_MS;

const createAuthReadinessTimeoutError = (): Error =>
    Object.assign(new Error('Timeout waiting for user'), {
        name: 'AuthReadinessTimeoutError',
    });

const createAuthReadinessCancelledError = (): Error =>
    Object.assign(new Error('Authentication readiness wait cancelled'), {
        name: 'AuthReadinessCancelledError',
    });

const createAuthAdmissionDeniedError = (): Error =>
    Object.assign(new Error('Full CRM access denied'), {
        name: 'AuthAdmissionDeniedError',
    });

type ReadinessCancellation = {
    cancelled: boolean;
    listeners: Set<() => void>;
};

type AuthenticationWaitOptions = {
    admissionRequired: boolean;
    deadlineMs: number;
    fetchUser: () => Promise<void>;
    needContextAccount: boolean;
    prepare?: () => Promise<void>;
    retryBudgetMs: number;
    signal?: AbortSignal;
};

/** Check if the error is worth retrying (chunk loads, network, 5xx). Let 401/403 fail fast. */
function isRetriableError(error: unknown): boolean {
    if (error instanceof Error) {
        const message = error.message.toLowerCase();

        if (message.includes('failed to fetch dynamically imported module')) {
            return true;
        }
    }

    if (error && typeof error === 'object') {
        // A response with a 5xx status is a transient server error — worth retrying.
        if ('response' in error) {
            const status = (error as { response?: { status?: number } }).response?.status;
            return status !== undefined && status >= 500;
        }

        // An axios error without a response is a network error or timeout (e.g. ERR_NETWORK,
        // ECONNABORTED) — also transient and worth retrying.
        const code = (error as { code?: unknown }).code;
        if (code === 'ERR_NETWORK' || code === 'ECONNABORTED') {
            return true;
        }
    }

    return false;
}

const authClient = new auth0.WebAuth({
    domain: import.meta.env.VITE_AUTH0_DOMAIN,
    redirectUri: `${window.location.origin}/auth/callback`,
    clientID: import.meta.env.VITE_AUTH0_CLIENT_ID,
    audience: import.meta.env.VITE_AUTH0_AUDIENCE,
    responseType: 'token',
    scope: 'openid internal-access api room:create',
});

/** Instantiate once per page load as a singleton. Relies on instance state for deduplication. */
class Auth {
    data: AuthData;

    private authUserFetchPromise: Promise<void> | null = null;

    private fullCrmAccessRequest: {
        token: string | null;
        promise: Promise<boolean>;
    } | null = null;

    constructor() {
        this.data = reactive<AuthData>({
            accessToken: localStorage.getItem(STORAGE_KEYS.TOKEN),
            expiration: localStorage.getItem(STORAGE_KEYS.TOKEN_EXPIRATION) || 0,
            impersonating: localStorage.getItem(STORAGE_KEYS.IMPERSONATING) === 'true',
            impersonateToken: localStorage.getItem(STORAGE_KEYS.IMPERSONATE_TOKEN),
        });
    }

    handshake(): void {
        if (this.handleSingleUseToken()) {
            return;
        }

        if (this.isExcludedRoute()) {
            return;
        }

        const now = new ActivixDate('now');
        const expiration = this.getExpirationDate();

        if (!expiration) {
            this.clearAndRedirectToLogin();
            return;
        }

        const refreshTreshold = expiration.subMinutes(30);

        if (now.isBetween(refreshTreshold, expiration)) {
            this.handleRefreshToken(true, true).catch(() => this.clearAndRedirectToLogin());
        } else if (!this.isAuthenticated()) {
            this.clearAndRedirectToLogin();
        } else {
            this.setAxiosToken();
        }
    }

    handleSingleUseToken(): boolean {
        const param = qs.parse(window.location.search, { ignoreQueryPrefix: true });

        if (!this.isAccessTokenValid() && param.tk) {
            this.storeAuthenticationTokens({
                accessToken: param.tk as string,
                expiresIn: Number(param.expires_in),
                singleUseToken: true,
            });
            return true;
        }

        return false;
    }

    isExcludedRoute(): boolean {
        const { pathname } = window.location;

        return (
            pathname === ROUTES.AUTH_ERROR ||
            pathname === ROUTES.AUTH_CALLBACK ||
            pathname === ROUTES.NEW_PASSWORD ||
            pathname.startsWith(ROUTES.LANDING) ||
            pathname.startsWith(ROUTES.SANDBOX)
        );
    }

    getExpirationDate(): ActivixDate | null {
        return this.data.expiration
            ? new ActivixDate(new Date(parseInt(String(this.data.expiration), 10)))
            : null;
    }

    clearAndRedirectToLogin(): void {
        this.clear();
        this.redirectToLogin({
            target: `${location.pathname}${location.search}`,
        });
    }

    redirectToLogin(customState: AppState): void {
        const payload: Record<string, unknown> = {
            appState: customState,
            connection: import.meta.env.VITE_AUTH0_CONNECTION,
        };

        const locale = localStorage.getItem(STORAGE_KEYS.LOCALE);
        if (locale) {
            payload.ui_locales = locale;
        }

        authClient.authorize(payload);
    }

    handleRefreshToken(autoRedirect = false, handshake = false): Promise<void> {
        return new Promise((resolve, reject) => {
            if (!handshake && this.impersonating()) {
                this.unimpersonate({ updateBackend: false });
                resolve();
                return;
            }

            authClient.checkSession({}, (error: unknown, authResult: AuthResult) => {
                if (error) {
                    this.clear();

                    if (autoRedirect) {
                        this.redirectToLogin({
                            target: `${location.pathname}${location.search}`,
                        });
                    } else {
                        showUnauthenticated();
                    }

                    reject(error);
                    return;
                }

                this.storeAuthenticationTokens(authResult, true);
                resolve();
            });
        });
    }

    handleAuthentication(): Promise<void> {
        return new Promise((resolve) => {
            authClient.parseHash(
                async (error: { errorDescription?: string } | null, authResult: AuthResult) => {
                    if (error) {
                        await router.push({
                            name: 'auth.error',
                            query: {
                                error: error.errorDescription,
                            },
                        });
                        resolve();
                        return;
                    }

                    this.storeAuthenticationTokens(authResult, true);

                    this.redirectToPreviousState(authResult).catch((e) => {
                        console.error('Failed to redirect after auth callback:', e);
                    });
                    resolve();
                }
            );
        });
    }

    setAxiosToken(): void {
        axios.defaults.headers.common.Authorization = `Bearer ${this.token()}`;
        if (this.impersonating()) {
            axios.defaults.headers.common.UnmaskedAuthorization = `Bearer ${this.unmaskedToken()}`;
        } else {
            delete axios.defaults.headers.common.UnmaskedAuthorization;
        }
    }

    storeAuthenticationTokens(authResult: AuthResult, skipRedirect = false): void {
        const expiration =
            new ActivixDate('now').addSeconds(authResult.expiresIn).timestamp! * 1000;

        this.data.accessToken = authResult.accessToken;
        this.data.expiration = expiration;

        localStorage.setItem(STORAGE_KEYS.TOKEN, authResult.accessToken);
        localStorage.setItem(STORAGE_KEYS.TOKEN_EXPIRATION, String(expiration));

        this.setAxiosToken();

        if (authResult.singleUseToken) {
            return;
        }

        if (!skipRedirect) {
            this.redirectToPreviousState(authResult);
        }
    }

    redirectToPreviousState(authResult: AuthResult): Promise<void> {
        const appState = authResult.appState || {};
        let target = appState.target || '/';

        if (target === ROUTES.LOGOUT) {
            target = '/';
        }

        return navigateForContext(target, (destination) => router.push(destination));
    }

    takeContextNavigationTarget(): string | null {
        return takeContextNavigationTarget();
    }

    async impersonate({ data }: { data: Record<string, unknown> }): Promise<void> {
        const { useTimedPageAction } = await import('@/hooks/useTimedPageAction.ts');
        const targetUserId = data.id ?? data.userId ?? null;
        const timedAction = useTimedPageAction({
            action: TimedPageActions.IMPERSONATE_USER,
            additionalData: {
                targetUserId: targetUserId === null ? null : String(targetUserId),
            },
        });
        try {
            this.clearCache();

            wait.start('auth.impersonating');

            await timedAction.run(async () => {
                const response = await axios.post('/v1/auth/impersonate', data);

                this.data.impersonating = true;
                this.data.impersonateToken = response.data.access_token;

                this.setAxiosToken();

                localStorage.setItem(STORAGE_KEYS.IMPERSONATE_TOKEN, this.data.impersonateToken!);
                localStorage.setItem(STORAGE_KEYS.IMPERSONATING, String(this.data.impersonating));

                await useUserStore().fetchAuthUser(true);
                await useAccountStore().reloadContextAccount(true);

                notify.success(
                    i18n.global.t('toastr.impersonateSuccess', [useUserStore().authUser.fullName])
                );
            });

            wait.end('auth.impersonating');
            eventBus.$emit('load-initial-data');
        } catch {
            wait.end('auth.impersonating');
            showWarning(i18n.global.t('toastr.cantImpersonate'));
        }
    }

    async unimpersonate({ updateBackend = true } = {}): Promise<void> {
        if (!this.impersonating()) {
            return;
        }

        this.clearCache();
        wait.start('auth.unimpersonating');

        const impersonatedId = useUserStore().authUser?.id || null;

        if (impersonatedId) {
            router.push({
                name: 'users.update',
                params: { id: impersonatedId },
            });
        }

        try {
            if (updateBackend) {
                await axios.post('/v1/auth/unimpersonate');
            }

            this.data.impersonating = false;
            this.data.impersonateToken = null;

            this.setAxiosToken();
            localStorage.removeItem(STORAGE_KEYS.IMPERSONATE_TOKEN);
            localStorage.removeItem(STORAGE_KEYS.IMPERSONATING);

            await useUserStore().fetchAuthUser(true);
            await useAccountStore().reloadContextAccount(true);

            nextTick(() => {
                eventBus.$emit('load-initial-data');
                wait.end('auth.unimpersonating');
            });
        } catch {
            wait.end('auth.unimpersonating');
        }
    }

    clearCache(): void {
        const caching = getCaching();

        if (caching) {
            caching.clear();
        }
    }

    clear(): void {
        localStorage.removeItem(STORAGE_KEYS.TOKEN);
        localStorage.removeItem(STORAGE_KEYS.TOKEN_EXPIRATION);
        localStorage.removeItem(STORAGE_KEYS.IMPERSONATE_TOKEN);
        localStorage.removeItem(STORAGE_KEYS.IMPERSONATING);

        this.clearCache();

        this.data.accessToken = null;
        this.data.expiration = null;
        this.data.impersonating = null;
        this.data.impersonateToken = null;

        useUserStore().clearAuthUser();
        useGlobalStore().resetState();
        ls.remove('context');
    }

    async logout(): Promise<void> {
        try {
            try {
                if (this.impersonating()) {
                    try {
                        await axios.post('/v1/auth/unimpersonate');
                    } finally {
                        // Send the final logout with the original token, even if teardown fails.
                        this.data.impersonating = false;
                        this.data.impersonateToken = null;
                        this.setAxiosToken();
                    }
                }
            } finally {
                await axios.post('logout');
            }
        } finally {
            this.redirectToLogout();
        }
    }

    async refreshFullCrmAccess(): Promise<boolean> {
        for (let attempt = 0; attempt < 2; attempt++) {
            const token = this.token();
            if (!token) {
                throw new Error('Authentication changed while refreshing CRM access');
            }

            if (this.fullCrmAccessRequest?.token !== token) {
                const request = {
                    token,
                    promise: authUserApi
                        .getFullCrmAccess()
                        .then(
                            (profile: { can_access_full_crm?: boolean }) =>
                                profile.can_access_full_crm === true
                        )
                        .finally(() => {
                            if (this.fullCrmAccessRequest === request) {
                                this.fullCrmAccessRequest = null;
                            }
                        }),
                };
                this.fullCrmAccessRequest = request;
            }

            try {
                // Each retry must wait for the previous token's request to settle.
                // eslint-disable-next-line no-await-in-loop
                const allowed = await this.fullCrmAccessRequest.promise;
                if (this.token() === token) {
                    return allowed;
                }
            } catch (error) {
                if (this.token() === token || !this.token()) {
                    throw error;
                }
            }
        }

        throw new Error('Authentication changed while refreshing CRM access');
    }

    async fetch(): Promise<void> {
        if (this.authUserFetchPromise !== null) {
            await this.authUserFetchPromise;
            return;
        }

        if (this.check() && !this.ready()) {
            this.authUserFetchPromise ??= this.fetchAuthUserWithRetry().finally(() => {
                this.authUserFetchPromise = null;
            });

            await this.authUserFetchPromise;
        }
    }

    async fetchAuthUserWithRetry(): Promise<void> {
        try {
            await useUserStore().fetchAuthUser();
        } catch (firstError) {
            if (!isRetriableError(firstError)) {
                throw firstError;
            }

            // Force bypass cache
            await useUserStore().fetchAuthUser(true);
        }
    }

    redirectToLogout(): void {
        this.clear();

        authClient.logout({
            returnTo: `${window.location.origin}/auth/logout`,
        });
    }

    isAccessTokenValid(): boolean {
        if (!this.data.accessToken || !this.data.expiration) {
            return false;
        }

        const now = new ActivixDate('now');
        const expiration = new ActivixDate(new Date(parseInt(String(this.data.expiration), 10)));

        return now.isBefore(expiration);
    }

    isAuthenticated(): boolean {
        if (this.impersonating()) {
            return true;
        }

        return this.isAccessTokenValid();
    }

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    user(): any {
        return useUserStore().parentAuthUser;
    }

    async waitForUser(
        needContextAccount = false,
        signal: AbortSignal | undefined = undefined
    ): Promise<unknown> {
        return this.waitForAuthentication({
            admissionRequired: false,
            deadlineMs: AUTH_READINESS_DEADLINE_MS,
            fetchUser: () => this.fetch(),
            needContextAccount,
            retryBudgetMs: AUTH_READINESS_RETRY_BUDGET_MS,
            signal,
        });
    }

    async waitForStartupAuthentication(
        signal: AbortSignal | undefined = undefined
    ): Promise<boolean> {
        if (!this.check()) {
            return true;
        }

        try {
            await this.waitForAuthentication({
                admissionRequired: true,
                deadlineMs: AUTH_STARTUP_DEADLINE_MS,
                fetchUser: () => measureStartupPhase('auth_accounts', () => this.fetch()),
                needContextAccount: false,
                prepare: async () => {
                    const allowed = await measureStartupPhase('admission', () =>
                        this.refreshFullCrmAccess()
                    );
                    if (!allowed) {
                        throw createAuthAdmissionDeniedError();
                    }
                },
                retryBudgetMs: AUTH_STARTUP_RETRY_BUDGET_MS,
                signal,
            });
            return true;
        } catch (error) {
            if (error instanceof Error && error.name === 'AuthAdmissionDeniedError') {
                return false;
            }
            throw error;
        }
    }

    private async waitForAuthentication({
        admissionRequired,
        deadlineMs,
        fetchUser,
        needContextAccount,
        prepare,
        retryBudgetMs,
        signal,
    }: AuthenticationWaitOptions): Promise<unknown> {
        const authenticatedAtStart = this.check();
        const fetchRequired = authenticatedAtStart && !this.ready();
        const finishAttempt = startAuthReadinessAttempt({
            admissionRequired,
            authenticatedAtStart,
            contextRequired: needContextAccount,
            deadlineMs,
            fetchRequired,
            retryBudgetMs,
        });

        if (signal?.aborted) {
            finishAttempt('cancelled');
            throw createAuthReadinessCancelledError();
        }

        const cancellation: ReadinessCancellation = {
            cancelled: false,
            listeners: new Set(),
        };
        const fetchPromise = (async () => {
            await prepare?.();

            if (this.hasContextIfNeeded(needContextAccount)) {
                return this.user();
            }

            if (this.check() && !this.ready()) {
                await fetchUser();
            }

            if (this.hasContextIfNeeded(needContextAccount)) {
                return this.user();
            }

            return this.pollUntilReady(needContextAccount, cancellation);
        })();

        let timeoutId: ReturnType<typeof setTimeout> | null = null;
        const timeoutPromise = new Promise<never>((_, reject) => {
            timeoutId = setTimeout(() => reject(createAuthReadinessTimeoutError()), deadlineMs);
        });
        let abortListener: (() => void) | null = null;
        const abortPromise = new Promise<never>((_, reject) => {
            if (!signal) {
                return;
            }
            abortListener = () => reject(createAuthReadinessCancelledError());
            signal.addEventListener('abort', abortListener, { once: true });
        });

        try {
            const user = await Promise.race([fetchPromise, timeoutPromise, abortPromise]);
            finishAttempt('ready');
            return user;
        } catch (error) {
            if (error instanceof Error && error.name === 'AuthReadinessTimeoutError') {
                finishAttempt('timeout');
                showConnectionTimeout();
            } else if (error instanceof Error && error.name === 'AuthReadinessCancelledError') {
                finishAttempt('cancelled');
            } else if (error instanceof Error && error.name === 'AuthAdmissionDeniedError') {
                finishAttempt('denied');
            } else {
                finishAttempt('failed');
            }
            throw error;
        } finally {
            cancellation.cancelled = true;
            cancellation.listeners.forEach((cancel) => cancel());
            cancellation.listeners.clear();
            if (timeoutId !== null) {
                clearTimeout(timeoutId);
            }
            if (signal && abortListener) {
                signal.removeEventListener('abort', abortListener);
            }
        }
    }

    private pollUntilReady(
        needContextAccount: boolean,
        cancellation: ReadinessCancellation
    ): Promise<unknown> {
        return new Promise((resolve) => {
            let timerId: ReturnType<typeof setTimeout> | null = null;
            const cancel = () => {
                if (timerId !== null) {
                    clearTimeout(timerId);
                }
                cancellation.listeners.delete(cancel);
                resolve(undefined);
            };
            const check = () => {
                if (cancellation.cancelled) {
                    cancel();
                    return;
                }

                if (this.hasContextIfNeeded(needContextAccount)) {
                    cancellation.listeners.delete(cancel);
                    resolve(this.user());
                    return;
                }

                timerId = setTimeout(check, WAIT_FOR_USER_INTERVAL_MS);
            };

            cancellation.listeners.add(cancel);
            timerId = setTimeout(check, WAIT_FOR_USER_INTERVAL_MS);
        });
    }

    check(): boolean {
        return this.isAuthenticated();
    }

    ready(): boolean {
        return this.isAuthenticated() && !!this.user()?.id;
    }

    /** Return true if the user is ready and the context account is loaded (when required). */
    hasContextIfNeeded(needContextAccount: boolean): boolean {
        return this.ready() && (!needContextAccount || !!useAccountStore().contextAccount);
    }

    token(): string | null {
        if (this.impersonating()) {
            return this.data.impersonateToken;
        }

        if (!this.isAccessTokenValid()) {
            return null;
        }

        return this.data.accessToken;
    }

    unmaskedToken(): string | null {
        if (!this.isAccessTokenValid()) {
            return null;
        }

        return this.data.accessToken;
    }

    audienceIsInSync(): boolean {
        const token = this.token();

        if (!token) {
            return false;
        }

        const tokenPayload = jwtDecode(token) as { aud?: string[] };
        const tokenAudience = tokenPayload?.aud || [];

        return tokenAudience.includes(import.meta.env.VITE_AUTH0_AUDIENCE!);
    }

    jwtSub(): string | false {
        const token = this.token();

        if (!token) {
            return false;
        }

        const tokenPayload = jwtDecode(token) as { sub: string };
        return tokenPayload.sub;
    }

    impersonating(): boolean {
        return !!this.data.impersonating;
    }
}

export default Auth;
export { readAuthContextAttributes } from './auth-context.ts';
